/**
 * Misconception Registry — module-based aggregation.
 *
 * The registry is split per module (rek, get, spr, sww, met, tijd, geld, taa)
 * to keep each file maintainable. This index concatenates all per-module
 * arrays and exposes the same public API that consumers used to import from
 * `../misconceptions`.
 */

import type { MisconceptionDefinition } from './types';
import { REK_MISCONCEPTIONS } from './rek';
import { GET_MISCONCEPTIONS } from './get';
import { SPR_MISCONCEPTIONS } from './spr';
import { SWW_MISCONCEPTIONS } from './sww';
import { MET_MISCONCEPTIONS } from './met';
import { TIJD_MISCONCEPTIONS } from './tijd';
import { GELD_MISCONCEPTIONS } from './geld';
import { TAA_MISCONCEPTIONS } from './taa';
export type { MisconceptionDefinition } from './types';

// ─── Central misconception registry ─────────────────────────────────────────────

export const MISCONCEPTION_REGISTRY: MisconceptionDefinition[] = [...REK_MISCONCEPTIONS, ...GET_MISCONCEPTIONS, ...SPR_MISCONCEPTIONS, ...SWW_MISCONCEPTIONS, ...MET_MISCONCEPTIONS, ...TIJD_MISCONCEPTIONS, ...GELD_MISCONCEPTIONS, ...TAA_MISCONCEPTIONS];

// ─── Helper functions ──────────────────────────────────────────────────────────

const MISCONCEPTION_BY_ID = new Map<string, MisconceptionDefinition>(MISCONCEPTION_REGISTRY.map(m => [m.id, m]));

/** Look up a misconception by id. Returns undefined if not registered. */
export function getMisconception(id: string): MisconceptionDefinition | undefined {
  return MISCONCEPTION_BY_ID.get(id);
}

/** Returns true if the given id is registered in the misconception registry. */
export function isKnownMisconception(id: string): boolean {
  return MISCONCEPTION_BY_ID.has(id);
}

/** Returns all misconceptions for a given module label (e.g. 'Metriek'). */
export function getMisconceptionsForModule(module: string): MisconceptionDefinition[] {
  return MISCONCEPTION_REGISTRY.filter(m => m.module === module);
}

/** Returns all registered misconception ids. */
export function getAllMisconceptionIds(): string[] {
  return MISCONCEPTION_REGISTRY.map(m => m.id);
}